import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, Select, Row, Col, Button, Checkbox, DatePicker, Input } from 'antd';
import './DashboardFilter.less';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useAuth } from '@isocel/front-common';
import moment from 'moment';

import { fetchStores } from '../../../queries/api/store';
import {
    Store,
    Package,
    PackagingFlowDirection,
    BaseModel,
    StoreUser,
    PackagingVoucherStatus,
} from '../../../queries/api/types';
import { useDashboardContext } from '../../../context/DashboardContext';
import { usePackagingConfig } from '../../../queries/package';
import { usePalettes, useSupplierByOrganization, useWarehousesByOrganization } from '../../../queries/palette';
import { useStoreByOrganization } from '../../../queries/store';
import { useSupplierByStore } from '../../../queries/api/supplier';
import { useNotification } from '../../notification/Notification';
import { PackagingFlowTypeMap } from '../../../constants';

const { Option } = Select;

const DashboardFilter: React.FC = () => {
    const [filteredStores, setFilteredStores] = useState<Store[]>([]);
    const [filteredSuppliers, setSuppliers] = useState<BaseModel[]>([]);
    const [filteredWarehouse, setWarehouse] = useState<BaseModel[]>([]);
    const [filteredParcPalett, setParcPalett] = useState<BaseModel[]>([]);
    const {
        selectedStoreIds,
        setSelectedStoreIds,
        selectedPackageIds,
        setSelectedPackageIds,
        selectedStatus,
        setSelectedStatus,
        differenceReported,
        setDifferenceReported,
        selectedSupplierIds,
        setSelectedSupplierIds,
        selectedParcPalettIds,
        setSelectedParcPalettIds,
        selectedWarehouseIds,
        setSelectedWarehouseIds,
        selectedStartDate,
        setSelectedStartDate,
        selectedEndDate,
        setSelectedEndDate,
        selectedVoucherStatus,
        setSelectedVoucherStatus,
        selectedLitigeStatus,
        setSelectedLitigeStatus,
        selectedBonDeTransport,
        setSelectedBonDeTransport,
        selectedInvoiceName,
        setSelectedInvoiceName,
        setStoreFilterManuallySet,
    } = useDashboardContext();
    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store;
    const [preSelectedStoreIds, setSelectedStoreIdsLocal] = useState<string[]>([]);
    const [preSelectedSupplierIds, setSelectedSupplierIdsLocal] = useState<string[]>([]);
    const [preSelectedWarehouseIds, setSelectedWarehouseIdsLocal] = useState<string[]>([]);
    const [preSelectedParcPaletteIds, setSelectedParcPaletteIdsLocal] = useState<string[]>([]);
    const [preSelectedPackages, setSelectedPackagesLocal] = useState<string[]>([]);
    const [preSelectedStatus, setSelectedStatusLocal] = useState<string[]>([]);
    const [preSelectedVoucherStatus, setSelectedVoucherStatusLocal] = useState<string[]>([]);
    const [preSelectedLitigeStatus, setSelectedLitigeStatusLocal] = useState<string[]>([]);
    const [preSelectedBonDeTransport, setSelectedBonDeTransportLocal] = useState<string>('');
    const [preSelectedInvoiceName, setSelectedInvoiceNameLocal] = useState<string>('');
    const [preDifferenceReported, setDifferenceReportedLocal] = useState<boolean>(false);
    const { data, error, isLoading } = usePackagingConfig(user?.organization?.id);
    const [packages, setPackages] = useState<Package[]>([]);

    const supplierByStoreData = useSupplierByStore(storeId || '', user?.organization?.id);
    const supplierByOrganizationData = useSupplierByOrganization(user?.organization?.id || '');
    const supplierData = storeId ? supplierByStoreData.data : supplierByOrganizationData.data;
    const { data: storesData } = useStoreByOrganization(user?.organization?.id ?? '');
    const { data: warehouseData } = useWarehousesByOrganization(user?.organization?.id || '');
    const { data: parcPaletteData } = usePalettes(user?.organization?.id || '');
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        if (data) {
            setPackages(data.packages);
        }
        setSelectedStatusLocal(selectedStatus);
        setDifferenceReportedLocal(differenceReported);
        setSelectedStoreIdsLocal(selectedStoreIds);
        setSelectedSupplierIdsLocal(selectedSupplierIds);
        setSelectedPackagesLocal(selectedPackageIds);
        setSelectedParcPaletteIdsLocal(selectedParcPalettIds);
        setSelectedWarehouseIdsLocal(selectedWarehouseIds);
        setSelectedVoucherStatusLocal(selectedVoucherStatus);
        setSelectedLitigeStatusLocal(selectedLitigeStatus);
        setSelectedBonDeTransportLocal(selectedBonDeTransport.join(';'));
        setSelectedInvoiceNameLocal(selectedInvoiceName);
        setEndDate(selectedEndDate);
        setStartDate(selectedStartDate);
    }, [
        data,
        differenceReported,
        selectedEndDate,
        selectedPackageIds,
        selectedParcPalettIds,
        selectedStartDate,
        selectedStatus,
        selectedStoreIds,
        selectedSupplierIds,
        selectedVoucherStatus,
        selectedWarehouseIds,
        selectedLitigeStatus,
        selectedBonDeTransport,
        selectedInvoiceName,
    ]);

    useEffect(() => {
        const fetchStoreDetails = async () => {
            if (storeId) {
                const stores = await fetchStores();
                const store = stores.find((store) => store.id === storeId);
                if (store) {
                    setFilteredStores([store]);
                    setSelectedStoreIdsLocal([store.id]);
                }
            }
        };

        fetchStoreDetails();
    }, [storeId]);
    const handleStoreSearch = useCallback(
        async (value: string) => {
            try {
                const filtered = value
                    ? storesData?.filter(
                          (store) =>
                              store.name.toLowerCase().includes(value.toLowerCase()) ||
                              store.code.toLowerCase().includes(value.toLowerCase())
                      )
                    : storesData;
                if (filtered) {
                    const sortedFiltered = filtered.sort((a, b) => {
                        const numA = parseInt(a.code, 10);
                        const numB = parseInt(b.code, 10);
                        if (!isNaN(numA) && !isNaN(numB)) {
                            return numA - numB;
                        }
                        return a.code.localeCompare(b.code);
                    });
                    setFilteredStores(sortedFiltered);
                }
            } catch (error) {
                console.error('Error fetching stores:', error);
            }
        },
        [storesData]
    );

    const handleSupplierSearch = useCallback(
        async (value: string) => {
            try {
                const filtered = value
                    ? supplierData?.filter((supplier) => supplier.name.toLowerCase().includes(value.toLowerCase()))
                    : supplierData;
                if (filtered) {
                    setSuppliers(filtered);
                }
            } catch (error) {
                console.error('Error fetching suppliers:', error);
            }
        },
        [supplierData]
    );
    const handleWarehouseSearch = useCallback(
        async (value: string) => {
            try {
                const filtered = value
                    ? warehouseData?.filter((warehouse) => warehouse.name.toLowerCase().includes(value.toLowerCase()))
                    : warehouseData;
                if (filtered) {
                    setWarehouse(filtered);
                }
            } catch (error) {
                console.error('Error fetching Warehouse:', error);
            }
        },
        [warehouseData]
    );

    const handlePalletSearch = useCallback(
        async (value: string) => {
            try {
                const filtered = value
                    ? parcPaletteData?.filter((palette) => palette.name.toLowerCase().includes(value.toLowerCase()))
                    : parcPaletteData;
                if (filtered) {
                    if (parcPaletteData) {
                        setParcPalett(parcPaletteData);
                    }
                }
            } catch (error) {
                console.error('Error fetching palette:', error);
            }
        },
        [parcPaletteData]
    );

    // Debounce the search functions using useMemo
    const debouncedStoreSearch = useMemo(() => debounce(handleStoreSearch, 300), [handleStoreSearch]);
    const debouncedWarehouseSearch = useMemo(() => debounce(handleWarehouseSearch, 300), [handleWarehouseSearch]);
    const debouncedSupplierSearch = useMemo(() => debounce(handleSupplierSearch, 300), [handleSupplierSearch]);
    const debouncedPaletteSearch = useMemo(() => debounce(handlePalletSearch, 300), [handlePalletSearch]);

    const handleSelectChange = (value: Array<{ key: string; value: string; label: string }>) => {
        setSelectedStoreIdsLocal(value.map((v) => v.value));
        if (value.length > 0) {
            setStoreFilterManuallySet(true);
        } else {
            setStoreFilterManuallySet(false);
        }
    };
    const handlePackageSelectChange = (value: string[]) => {
        setSelectedPackagesLocal(value);
    };
    const handleStatusChange = (value: Array<{ key: string; label: string; value: string }>) => {
        const selectedStatus = value.map((item) => item.value).filter((val) => val !== undefined);
        setSelectedStatusLocal(selectedStatus);
    };
    const handleDifferenceReportedChange = (e: any) => {
        setDifferenceReportedLocal(e.target.checked);
    };
    const handleVoucherStatusChange = (value: string[]) => {
        setSelectedVoucherStatusLocal(value);
    };
    const handleLitigeStatusChange = (value: string[]) => {
        setSelectedLitigeStatusLocal(value);
    };
    const handleBonDeTransportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedBonDeTransportLocal(e.target.value);
    };
    const handleInvoiceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedInvoiceNameLocal(e.target.value);
    };
    const applyFilters = () => {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            showNotification(
                'error',
                "Échec de l'analyse",
                `Veuillez choisir une date de début antérieure à la date de fin`
            );
            return;
        }
        setSelectedStoreIds(storeId ? [storeId] : preSelectedStoreIds);
        setSelectedSupplierIds(preSelectedSupplierIds);
        setSelectedPackageIds(preSelectedPackages);
        setSelectedStatus(preSelectedStatus);
        setDifferenceReported(preDifferenceReported);
        setSelectedParcPalettIds(preSelectedParcPaletteIds);
        setSelectedWarehouseIds(preSelectedWarehouseIds);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setSelectedVoucherStatus(preSelectedVoucherStatus);
        setSelectedLitigeStatus(preSelectedLitigeStatus);
        const bonDeTransportArray = preSelectedBonDeTransport
            .split(';')
            .map((voucher) => voucher.trim())
            .filter((voucher) => voucher.length > 0);
        setSelectedBonDeTransport(bonDeTransportArray);
        setSelectedInvoiceName(preSelectedInvoiceName);
    };
    const handleStartDate = (values: any) => {
        if (values) {
            setStartDate(values);
        } else {
            setStartDate(null);
        }
    };
    const handleEndDate = (values: any) => {
        if (values) {
            setEndDate(values);
        } else {
            setEndDate(null);
        }
    };
    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error loading package data</div>;

    return (
        <div className="dashboard-filter">
            <Form layout="vertical">
                <h2>Filtrer</h2>
                <Row gutter={16}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="Statut" className="bold-label">
                            <Select
                                mode="multiple"
                                placeholder="Tous les statuts"
                                notFoundContent="Aucun résultat"
                                onChange={handleStatusChange}
                                value={preSelectedStatus.map((status) => {
                                    const label = PackagingFlowTypeMap.get(Number(status))?.label ?? status;
                                    return {
                                        key: status,
                                        label: label,
                                        value: status,
                                    };
                                })}
                                labelInValue
                            >
                                {Object.keys(PackagingFlowDirection)
                                    .filter((key) => !isNaN(Number(PackagingFlowDirection[key as any])))
                                    .map((key) => {
                                        const value =
                                            PackagingFlowDirection[key as keyof typeof PackagingFlowDirection];
                                        const label = PackagingFlowTypeMap.get(value)?.label ?? key;
                                        return (
                                            <Option key={value} value={value}>
                                                {label}
                                            </Option>
                                        );
                                    })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label=" ">
                            <Checkbox onChange={handleDifferenceReportedChange} checked={preDifferenceReported}>
                                Écart constaté
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="Types d'emballages" className="bold-label">
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="Sélectionner un emballage"
                                notFoundContent="Aucun résultat"
                                filterOption={(input, option) =>
                                    option?.children
                                        ? option.children.toString().toLowerCase().includes(input.toLowerCase())
                                        : false
                                }
                                onChange={handlePackageSelectChange}
                                value={preSelectedPackages}
                            >
                                {packages.map((pkg) => (
                                    <Option key={pkg.id} value={pkg.id}>
                                        {pkg.type.name === pkg.model.name
                                            ? pkg.type.name
                                            : `${pkg.type.name} + ${pkg.model.name}`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="Magasin" className="bold-label">
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="Rechercher un magasin"
                                onSearch={debouncedStoreSearch}
                                filterOption={false}
                                maxTagCount="responsive"
                                suffixIcon={<SearchOutlined />}
                                notFoundContent="Aucun résultat"
                                onChange={handleSelectChange}
                                value={(storeId ? [storeId] : preSelectedStoreIds)
                                    .filter((id) => !!id) // filter out empty, null, or undefined IDs
                                    .map((id) => {
                                        let store = filteredStores.find((s) => s.id === id);
                                        if (!store && storesData) {
                                            store = storesData.find((s) => s.id === id);
                                        }
                                        return store
                                            ? { key: store.id, value: store.id, label: `${store.code} - ${store.name}` }
                                            : { key: id, value: id, label: id };
                                    })}
                                labelInValue
                                disabled={!!storeId}
                                onDropdownVisibleChange={(open) => {
                                    if (open) {
                                        handleStoreSearch('');
                                    }
                                }}
                            >
                                {filteredStores.map((store) => (
                                    <Option key={store.id} value={store.id}>
                                        {store.code} - {store.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="Transporteur" className="bold-label">
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="Rechercher un Transporteur"
                                onSearch={debouncedSupplierSearch}
                                filterOption={false}
                                maxTagCount="responsive"
                                notFoundContent="Aucun résultat"
                                suffixIcon={<SearchOutlined />}
                                onChange={(value) => setSelectedSupplierIdsLocal(value.map((v: any) => v.value))}
                                value={preSelectedSupplierIds.map((id) => {
                                    let supplier = filteredSuppliers.find((s) => s.id === id);
                                    if (!supplier && supplierData) {
                                        supplier = supplierData.find((s: any) => s.id === id);
                                    }
                                    return supplier
                                        ? { key: supplier.id, value: supplier.id, label: supplier.name }
                                        : { key: id, value: id, label: id };
                                })}
                                labelInValue
                                onDropdownVisibleChange={(open) => {
                                    if (open) {
                                        handleSupplierSearch('');
                                    }
                                }}
                            >
                                {filteredSuppliers.map((supplier) => (
                                    <Option key={supplier.id} value={supplier.id}>
                                        {supplier.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={6}>
                        <Form.Item label="Période Date de début" className="bold-label">
                            <DatePicker
                                onChange={handleStartDate}
                                placeholder={'Date de début'}
                                format="DD/MM/YYYY"
                                value={startDate ? moment(startDate) : null}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={6}>
                        <Form.Item label="Période Date de fin" className="bold-label">
                            <DatePicker
                                onChange={handleEndDate}
                                placeholder={'Date de fin'}
                                format="DD/MM/YYYY"
                                value={endDate ? moment(endDate) : null}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        {!storeId && (
                            <Form.Item label="Parc palette" className="bold-label">
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Parc palette"
                                    onSearch={debouncedPaletteSearch}
                                    filterOption={false}
                                    maxTagCount="responsive"
                                    suffixIcon={<SearchOutlined />}
                                    notFoundContent="Aucun résultat"
                                    onChange={(value) => setSelectedParcPaletteIdsLocal(value.map((v: any) => v.value))}
                                    value={preSelectedParcPaletteIds.map((id) => {
                                        let palette = filteredParcPalett.find((s) => s.id === id);
                                        if (!palette && parcPaletteData) {
                                            palette = parcPaletteData.find((s: any) => s.id === id);
                                        }
                                        return palette
                                            ? { key: palette.id, value: palette.id, label: palette.name }
                                            : { key: id, value: id, label: id };
                                    })}
                                    labelInValue
                                    onDropdownVisibleChange={(open) => {
                                        if (open) {
                                            handlePalletSearch('');
                                        }
                                    }}
                                >
                                    {filteredParcPalett.map((supplier) => (
                                        <Option key={supplier.id} value={supplier.id}>
                                            {supplier.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        {!storeId && (
                            <Form.Item label="Entrepôt" className="bold-label">
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Entrepôt"
                                    onSearch={debouncedWarehouseSearch}
                                    filterOption={false}
                                    maxTagCount="responsive"
                                    suffixIcon={<SearchOutlined />}
                                    onChange={(value) => setSelectedWarehouseIdsLocal(value.map((v: any) => v.value))}
                                    notFoundContent="Aucun résultat"
                                    value={preSelectedWarehouseIds.map((id) => {
                                        let warehouse = filteredWarehouse.find((w) => w._id === id);
                                        if (!warehouse && warehouseData) {
                                            warehouse = warehouseData.find((w: any) => w._id === id);
                                        }
                                        return warehouse
                                            ? { key: warehouse._id, value: warehouse._id, label: warehouse.name }
                                            : { key: id, value: id, label: id };
                                    })}
                                    labelInValue
                                    disabled={!!storeId}
                                    onDropdownVisibleChange={(open) => {
                                        if (open) {
                                            handleWarehouseSearch('');
                                        }
                                    }}
                                >
                                    {filteredWarehouse.map((warehouse) => (
                                        <Option key={warehouse._id} value={warehouse._id}>
                                            {warehouse.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="État du bon d'emballages" className="bold-label">
                            <Select
                                mode="multiple"
                                placeholder="Sélectionner un état"
                                notFoundContent="Aucun résultat"
                                onChange={handleVoucherStatusChange}
                                value={preSelectedVoucherStatus}
                            >
                                <Option value={PackagingVoucherStatus.Used}>{PackagingVoucherStatus.Used}</Option>
                                <Option value={PackagingVoucherStatus.NotUsed}>{PackagingVoucherStatus.NotUsed}</Option>
                                <Option value={PackagingVoucherStatus.Expired}>{PackagingVoucherStatus.Expired}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                        <Form.Item label="Statut litige" className="bold-label">
                            <Select
                                mode="multiple"
                                placeholder="Tous les statuts litige"
                                notFoundContent="Aucun résultat"
                                onChange={handleLitigeStatusChange}
                                value={preSelectedLitigeStatus}
                            >
                                <Option value="Non vérifié">Non vérifié</Option>
                                <Option value="Accepté">Accepté</Option>
                                <Option value="Refusé">Refusé</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    {/* Added transport voucher filter */}
                    <Col span={12}>
                        <Form.Item label="Bon de transport" className="bold-label">
                            <Input
                                placeholder="Bon de transport"
                                value={preSelectedBonDeTransport}
                                onChange={handleBonDeTransportChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Numéro lot facturation" className="bold-label">
                            <Input
                                placeholder="Numéro lot facturation"
                                value={preSelectedInvoiceName}
                                onChange={handleInvoiceNameChange}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button type="primary" onClick={applyFilters}>
                            Appliquer les filtres
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default DashboardFilter;
